import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import Joi from "joi-browser";
import clsx from "clsx";
import React, { Component } from "react";
import { Checkbox, CheckboxField } from "../Catalyst/checkbox";
import { Description, FieldGroup, Label } from "../Catalyst/fieldset";
import { General } from "../../common/constants";
import { isEmpty } from "../../common/utils";
import IconButton from "../Actions/IconButton";
import { PellerexButton } from "../Actions/PellerexButton";
import AnimationClip from "../Animations/AnimationClip";
import Dropdown from "../Elements/Dropdown";
import AutoComplete from "../Search/AutoComplete";
import DateTime from "./DateTime";
import Input from "./Input";
import { subscribeToTheme } from "../../common/theme";
import RadioGroup from "./RadioGroup";
import TagInput from "./TagInput";
import TileGroup from "./TileGroup";

class Form extends Component {
  fileInputRef = React.createRef();
  constructor(props) {
    super(props);
    this.state = {
      ///Used to track the final form values to be submitted
      data: {},

      //Used to track the interim changes not critical to the final values submitted (e.g. text changes in autocomplete)
      interimData: {},
      errorList: {},
    };
  }

  // Stripe Elements render inside Stripe-hosted iframes, so the theme's CSS tokens
  // cannot reach them — the resolved colours must be read out and passed as options.
  // Recomputed per render; componentDidMount re-renders the form on theme changes.
  stripeElementsStyles = () => {
    const readToken = (name, fallback) =>
      (typeof window !== "undefined"
        ? window.getComputedStyle(document.documentElement).getPropertyValue(name).trim()
        : "") || fallback;
    const foreground = readToken("--foreground", "#18181b");
    const placeholder = readToken("--foreground-subtle", "#a1a1aa");

    return {
      iconStyle: "solid",
      style: {
        base: {
          iconColor: foreground,
          color: foreground,
          fontWeight: 300,
          fontFamily: "'Lato', 'Helvetica Neue', Arial, Helvetica, sans-serif",
          fontSize: "16px",
          fontSmoothing: "antialiased",
          ":-webkit-autofill": {
            color: "#fce883",
          },
          "::placeholder": {
            color: placeholder,
          },
          border: "1px solid rgba(34, 36, 38, 0.15)",
        },
        invalid: {
          iconColor: "#c80b0b",
          color: "#c80b0b",
        },
      },
    };
  };

  componentDidMount() {
    // Stripe iframes only restyle when their options prop changes — re-render on theme flips.
    this.unsubscribeTheme = subscribeToTheme(() => this.forceUpdate());
  }

  componentWillUnmount() {
    if (this.unsubscribeTheme) {
      this.unsubscribeTheme();
    }
  }

  fullValidation = () => {
    let standardFormValidation = this.validateStandardObjects();
    let paymentValidation = this.validatePaymentObjects();

    if (standardFormValidation === null && paymentValidation == null) {
      return null;
    }
    return "There were validation errors on the page";
  };

  validateStandardObjects = () => {
    const joiOptions = {
      abortEarly: false,
      allowUnknown: true,
    };
    let result = {};
    const { error } = Joi.validate(this.state.data, this.schema, joiOptions);
    if (error && error.details && error.details.some((a) => a)) {
      for (var item in error.details) {
        result[error.details[item].path[0]] = error.details[item].message;
      }
    }

    if (isEmpty(result)) {
      return null;
    }

    return result;
  };

  validatePaymentObjects = () => {
    if (
      !this.state.data?.cardNumber &&
      !this.state.data?.cardCvc &&
      !this.state.data?.cardExpiry
    ) {
      if (isEmpty(this.state.stateErrors)) {
        return null;
      }

      return this.state.stateErrors;
    } else {
      if (
        this.state.data?.cardNumber?.complete &&
        this.state.data?.cardCvc?.complete &&
        this.state.data?.cardExpiry?.complete
      ) {
        return null;
      } else {
        return "There were validation errors on the page";
      }
    }
  };

  renderSubText({ subText, subTextVisible, subTextType = 'info' }) {
    if (!subTextVisible || !subText) return null;
    const subTextTypeClasses = {
      error: 'text-text-error',
      success: 'text-text-success',
      info: 'text-text-info',
    };
    return (
      <div className={clsx('text-sm mt-1', subTextTypeClasses[subTextType] || subTextTypeClasses.info)}>
        {subText}
      </div>
    );
  }

  renderSubmitButton({
    name,
    form,
    disabled,
    color,
    className,
    buttonClassName,
    onClick,
    loading = false,
  }) {
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <PellerexButton
          type="submit"
          disabled={disabled || this.fullValidation() !== null || loading}
          name={name}
          loading={loading}
          form={form}
          onClick={onClick}
          color={color}
          className={buttonClassName}
        >
          {name}
        </PellerexButton>
      </div>
    );
  }

  isFormValid() {
    return this.fullValidation() === null;
  }

  // Wrapper component for grouping form fields with consistent vertical spacing
  renderFieldGroup({ children, className }) {
    return (
      <FieldGroup className={className}>
        {children}
      </FieldGroup>
    );
  }

  renderButton({ name, className, disabled, onClick }) {
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <PellerexButton disabled={disabled} name={name} onClick={onClick}>
          {name}
        </PellerexButton>
      </div>
    );
  }

  renderCheckbox({
    name,
    label,
    description,
    requiredFlag,
    onChange,
    disabled,
    disabledReason,
    onDisabledLinkClick,
    disabledLinkText,
    styles,
    className,
    placeholder,
    subText,
    subTextVisible,
    subTextType,
    loading = false,
  }) {
    const { data, stateErrors } = this.state;
    const isChecked = data[name] ?? false;
    const showDisabledIndicator = disabled && disabledReason;
    
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <CheckboxField>
          <Checkbox
            name={name}
            checked={isChecked}
            onChange={(checked) => {
              this.setState({
                ...this.state,
                data: { ...this.state.data, [name]: checked }
              });
              if (onChange) {
                onChange({ name, value: checked });
              }
            }}
            disabled={disabled}
            color="indigo"
          />
          <Label className={disabled ? "text-foreground-subtle" : ""}>
            {label}
            {requiredFlag && <span className="text-red-500 dark:text-red-400"> *</span>}
            {showDisabledIndicator && (
              <span 
                title={disabledReason}
                className="ml-1.5 inline-flex items-center cursor-help"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 20 20" 
                  fill="currentColor" 
                  className="w-4 h-4 text-amber-500 dark:text-amber-400"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </span>
            )}
          </Label>
          {description && (
            <Description className={disabled ? "text-foreground-subtle" : ""}>
              {description}
              {showDisabledIndicator && onDisabledLinkClick && (
                <a 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onDisabledLinkClick();
                  }}
                  className="ml-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-400 hover:underline font-medium"
                >
                  {disabledLinkText || "Upgrade"}
                </a>
              )}
            </Description>
          )}
        </CheckboxField>
        {stateErrors && stateErrors[name] && (
          <p className="text-sm mt-1 text-text-error">{stateErrors[name]}</p>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderInput({
    name,
    label,
    className,
    type = "text",
    onChange,
    auxillaryButtonClicked,
    auxillaryButtonType,
    auxillaryButtonColor,
    placeholder,
    requiredFlag,
    readyOnly,
    onBlur,
    subIconVisible,
    subText,
    subTextVisible,
    subTextType,
    disabled = false,
    autoComplete,
    autoFocus = false,
    loading = false,
    externalError = null,
    status = null,
    statusTooltip = '',
    ignorePasswordManagers = false,
  }) {
    const { data, stateErrors } = this.state;
    const error = externalError || (stateErrors ? stateErrors[name] || "" : "");
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <Input
          key={name}
          name={name}
          label={label}
          readyOnly={readyOnly}
          requiredFlag={requiredFlag}
          subIconVisible={subIconVisible}
          onChange={this.handleStandardObjects_InputChange(onChange)}
          value={data[name]}
          error={error}
          className={className}
          type={type}
          disabled={disabled}
          placeholder={placeholder}
          onBlur={onBlur}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          loading={loading}
          status={status}
          statusTooltip={statusTooltip}
          ignorePasswordManagers={ignorePasswordManagers}
        ></Input>

        {auxillaryButtonType && (
          <div className="absolute right-2 top-[1.85rem]">
            <IconButton
              name={name}
              disabled={disabled}
              type={auxillaryButtonType}
              color={auxillaryButtonColor}
              onClick={(e) => {
                if (!disabled) {
                  auxillaryButtonClicked({ name });
                }
              }}
              size={18}
            />
          </div>
        )}

        {subIconVisible && !loading && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderFileUpload({
    name,
    label,
    className,
    styles,
    subIconVisible,
    requiredFlag,
    onFileUpdated,
    disabled = false,
    subText,
    subTextVisible,
    subTextType,
    loading = false,
  }) {
    const { data, stateErrors } = this.state;

    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <Input
          name={name}
          label={label}
          requiredFlag={requiredFlag}
          onChange={(e) => this.handleFileUpload_InputChange(e, onFileUpdated)}
          value={data[name]}
          error={stateErrors ? stateErrors[name] || "" : ""}
          className={styles}
          type={"file"}
          disabled={disabled}
          placeholder={label}
          fileInputReference={this.fileInputRef}
        ></Input>
        {subIconVisible && !loading && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderDateTme({
    name,
    label,
    type = General.DateTimeSettings.Component.FullDateTime,
    placeholder,
    subIconVisible,
    requiredFlag,
    minDate,
    maxDate,
    className,
    subText,
    subTextVisible,
    subTextType,
    loading = false,
  }) {
    const { data, stateErrors } = this.state;
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <DateTime
          name={name}
          label={label}
          placeholder={placeholder}
          requiredFlag={requiredFlag}
          value={data[name]}
          error={stateErrors ? stateErrors[name] || "" : ""}
          iconPosition="left"
          type={type}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={General.DateTimeSettings.DateFormat}
          onChange={this.handleDateObjects_InputChange}
          closable={true}
        />
        {subIconVisible && !loading && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderDropDown({
    name,
    label,
    hideLabel,
    className,
    options,
    search,
    disabled = false,
    loading = false,
    requiredFlag,
    subIconVisible,
    selectionChangedEvent,
    defaultValue,
    placeholder,
    subText,
    subTextVisible,
    subTextType,
  }) {
    const { data, stateErrors } = this.state;
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <Dropdown
          id={name}
          name={name}
          label={label}
          disabled={disabled}
          loading={loading}
          hideLabel={hideLabel}
          requiredFlag={requiredFlag}
          variant="outlined"
          options={options}
          search={search}
          selection
          defaultValue={defaultValue}
          error={stateErrors ? stateErrors[name] || "" : ""}
          value={data[name]}
          onChange={this.handleDropDown_Change(label, selectionChangedEvent)}
          placeholder={placeholder}
          fluid
        ></Dropdown>
        {subIconVisible && !loading && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderAutoComplete({
    name,
    label,
    className,
    results,
    subText,
    subTextVisible,
    subTextType,
    subTextClass,
    valueField,
    textField,
    placeholder,
    resultRenderer,
    renderSelection,
    handleResultSelect,
    handleSearchChange,
    onBlur,
    loading,
    subIconVisible,
    requiredFlag,
  }) {
    const { data, interimData, stateErrors } = this.state;
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <AutoComplete
          id={name}
          name={name}
          label={label}
          valueField={valueField}
          textField={textField}
          requiredFlag={requiredFlag}
          placeholder={placeholder}
          subText={subText}
          subTextVisible={subTextVisible}
          subTextClass={subTextClass}
          error={stateErrors ? stateErrors[name] || "" : ""}
          loading={loading}
          value={interimData?.hasOwnProperty(name) ? interimData[name] : ""}
          handleSearchChange={this.handleAutoComplete_SearchChange(
            label,
            handleSearchChange
          )}
          results={results}
          onBlur={onBlur}
          handleResultSelect={this.handleAutoComplete_SelectionChange(
            label,
            handleResultSelect
          )}
          renderSelection={renderSelection}
          resultRenderer={resultRenderer}
        />
        {subIconVisible && !loading && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderTileGroup({
    name,
    label,
    options,
    defaultValue,
    className,
    columns,
    selectionChangedEvent,
    subText,
    subTextVisible,
    subTextType,
    loading = false,
  }) {
    const { data, stateErrors } = this.state;
    const error = stateErrors ? stateErrors[name] : null;
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        {label && (
          <label
            htmlFor={name}
            className="mb-2 block text-sm/6 font-medium text-foreground dark:text-white"
          >
            {label}
          </label>
        )}
        <TileGroup
          name={name}
          options={options}
          value={data[name] ?? defaultValue}
          columns={columns}
          loading={loading}
          error={error}
          onChange={this.handleDropDown_Change(label, selectionChangedEvent)}
        />
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderRadioGroup({
    name,
    label,
    children,
    icon,
    requiredFlag,
    defaultValue,
    subIconVisible,
    className,
    selectionChangedEvent,
    subText,
    subTextVisible,
    subTextType,
    loading = false,
  }) {
    const { data, stateErrors } = this.state;
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <RadioGroup
          id={name}
          key={name}
          icon={icon}
          requiredFlag={requiredFlag}
          name={name}
          children={children}
          value={data[name] ?? defaultValue}
          onChange={this.handleRadioGroup_Change(
            label,
            selectionChangedEvent
          )}
        ></RadioGroup>
        {subIconVisible && !loading && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderTagInput({
    name,
    label,
    className,
    subIconVisible,
    requiredFlag,
    itemAddedEvent,
    defaultValue,
    placeholder,
    subText,
    subTextVisible,
    subTextType,
    loading = false,
  }) {
    const { data, stateErrors } = this.state;
    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <TagInput
          id={name}
          name={name}
          requiredFlag={requiredFlag}
          label={label}
          variant="outlined"
          options={data[name]}
          // onAddItem={this.handleTagInput_Add(label, itemAddedEvent)}
          onChange={this.handleTagInput_Add(label, itemAddedEvent)}
          search
          selection
          multiple
          allowAdditions
          defaultValue={defaultValue}
          placeholder={placeholder}
          error={stateErrors ? stateErrors[name] || "" : ""}
          rows={4}
          fluid
        ></TagInput>
        {subIconVisible && !loading && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderCardNumber({
    name,
    label,
    className,
    requiredFlag,
    subIconVisible = false,
    type = "text",
    subText,
    subTextVisible,
    subTextType,
  }) {
    const { data, stateErrors } = this.state;
    let error = stateErrors ? stateErrors[name] || "" : "";

    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <label className="text-foreground-muted text-sm" htmlFor={name}>
          {label}
          {requiredFlag && <span className="text-red-500 dark:text-red-400"> *</span>}
        </label>
        <CardNumberElement
          id={name}
          onBlur={() => {}}
          onChange={this.handlePayment_InputChange(name, label)}
          onFocus={() => {}}
          onReady={() => {}}
          options={this.stripeElementsStyles()}
        />
        {subIconVisible && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {error && <p className="text-sm mt-1 !text-text-error">{error}</p>}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderCardExpiryDate({
    name,
    label,
    requiredFlag,
    className,
    subIconVisible = false,
    type = "text",
    subText,
    subTextVisible,
    subTextType,
  }) {
    const { data, stateErrors } = this.state;
    let error = stateErrors ? stateErrors[name] || "" : "";

    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <label className="text-foreground-muted text-sm" htmlFor={name}>
          {label}
          {requiredFlag && <span className="text-red-500 dark:text-red-400"> *</span>}
        </label>
        <CardExpiryElement
          id={name}
          onBlur={() => {}}
          onChange={this.handlePayment_InputChange(name, label)}
          onFocus={() => {}}
          onReady={() => {}}
          options={this.stripeElementsStyles()}
        />
        {subIconVisible && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {error && <p className="text-sm mt-1 !text-text-error">{error}</p>}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  renderCardCvc({
    name,
    label,
    className,
    requiredFlag,
    subIconVisible = false,
    type = "text",
    subText,
    subTextVisible,
    subTextType,
  }) {
    const { data, stateErrors } = this.state;
    let error = stateErrors ? stateErrors[name] || "" : "";

    return (
      <div className={`relative mb-4 ${className || ''}`}>
        <label className="text-foreground-muted text-sm" htmlFor={name}>
          {label}
          {requiredFlag && <span className="text-red-500 dark:text-red-400"> *</span>}
        </label>
        <CardCvcElement
          id={name}
          onBlur={() => {}}
          onChange={this.handlePayment_InputChange(name, label)}
          onFocus={() => {}}
          onReady={() => {}}
          options={this.stripeElementsStyles()}
        />
        {subIconVisible && (
          <div className="absolute -top-2.5 right-2.5">
            <AnimationClip type="success" width={50} height={50} />
          </div>
        )}
        {error && <p className="text-sm mt-1 !text-text-error">{error}</p>}
        {this.renderSubText({ subText, subTextVisible, subTextType })}
      </div>
    );
  }

  handleSubmit = (e, controlName = "") => {
    e.preventDefault();

    //Validation for all the controls
    if (!controlName) {
      if (!isEmpty(this.state.stateErrors)) {
        return false;
      }
      return true;
    }
    //Validation only for one control
    else {
      if (
        !isEmpty(this.state.stateErrors) &&
        this.state.stateErrors[controlName]
      ) {
        return false;
      }
      return true;
    }
  };

  // Re-validates fields against the CURRENT state data. Handlers that sanitise
  // what the user typed must call this after storing the cleaned value: the
  // keystroke validation ran against the raw input, so its error can outlive
  // the character the sanitiser swallowed.
  revalidateFields = (...fieldNames) => {
    const errorList = this.validateStandardObjects() || {};
    this.setState((prevState) => {
      const stateErrors = { ...(prevState.stateErrors || {}) };
      for (const field of fieldNames) {
        if (errorList[field]) {
          stateErrors[field] = errorList[field];
        } else {
          delete stateErrors[field];
        }
      }
      return { ...prevState, stateErrors };
    });
  };

  handleStandardObjects_InputChange =
    (onChange) =>
    ({ currentTarget: input }) => {
      let data = this.state.data;
      data[input.name] = input.value;
      this.setState({
        ...this.state,
        data,
      });

      let stateErrors = this.state.stateErrors || {};
      let errorList = this.validateStandardObjects();
      if (errorList && errorList[input.name]) {
        stateErrors[input.name] = errorList[input.name];
      } else {
        delete stateErrors[input.name];
      }

      this.setState({
        ...this.state,
        stateErrors,
      });

      if (onChange) {
        onChange({
          value: input.value,
          name: input.name,
        });
      }

      // A sanitising onChange may have replaced the raw value validated above
      // (a swallowed space must not leave its error behind); re-run this
      // field's verdict after all batched updates commit.
      this.setState({}, () => this.revalidateFields(input.name));
    };

  handleFileUpload_InputChange = ({ currentTarget: input }, onFileUpdated) => {
    //store values
    let data = this.state.data;
    data[input.name] = input.files;
    this.setState({
      ...this.state,
      data,
    });

    let stateErrors = this.state.stateErrors || {};
    let errorList = this.validateStandardObjects();
    if (errorList && errorList[input.name]) {
      stateErrors[input.name] = errorList[input.name];
    } else {
      delete stateErrors[input.name];
    }

    this.setState({
      ...this.state,
      stateErrors,
    });
    if (onFileUpdated) {
      onFileUpdated();
    }
  };

  handlePayment_InputChange = (name, label) => (elementData) => {
    //store values

    this.state.data[name] = elementData;

    let stateErrors = this.state.stateErrors || {};

    if (!elementData.complete && !elementData.error) {
      stateErrors[name] = `Your ${label} is incomplete or invalid.`;
    } else if (elementData.complete && !elementData.error) {
      delete stateErrors[name];
    }

    this.setState({
      ...this.state,
      stateErrors,
    });
  };

  handleCheckboxObjects_InputChange =
    (onChange) =>
    (e, { checked, name }) => {
      //store values
      let data = this.state.data;
      data[name] = checked;
      this.setState({
        ...this.state,
        data,
      });

      let stateErrors = this.state.errorList || {};
      let errorList = this.validateStandardObjects();
      if (errorList && errorList[name]) {
        stateErrors[name] = errorList[name];
      } else {
        delete stateErrors[name];
      }

      this.setState({
        ...this.state,
        stateErrors,
      });

      if (onChange) {
        onChange({ checked, name });
      }
    };

  handleDateObjects_InputChange = (event, { name, value }) => {
    //store values
    let data = this.state.data;
    data[name] = value;
    this.setState({
      ...this.state,
      data,
    });

    let stateErrors = this.state.errorList || {};
    let errorList = this.validateStandardObjects();
    if (errorList && errorList[name]) {
      stateErrors[name] = errorList[name];
    } else {
      delete stateErrors[name];
    }

    this.setState({
      ...this.state,
      stateErrors,
    });
  };

  handleDropDown_Change = (label, selectionChange) => (e, data) => {
    let stateData = this.state.data;
    stateData[data.name] = data.value;
    this.setState({
      ...this.state,
      data: stateData,
    });

    let stateErrors = this.state.stateErrors || {};
    let errorList = this.validateStandardObjects();
    if (errorList && errorList[data.name]) {
      stateErrors[data.name] = errorList[data.name];
    } else {
      delete stateErrors[data.name];
    }

    this.setState({
      ...this.state,
      stateErrors,
    });

    if (selectionChange) {
      selectionChange(data.value);
    }
  };

  handleAutoComplete_SelectionChange =
    (label, selectionChange) => (e, data) => {
      let stateData = this.state.data;
      stateData[data.name] = data.value;

      let stateInterimData = this.state.interimData;

      stateInterimData[data.name] = data.text;

      this.setState({
        ...this.state,
        data: stateData,
        interimData: stateInterimData,
      });

      let stateErrors = this.state.stateErrors || {};
      let errorList = this.validateStandardObjects();
      if (errorList && errorList[data.name]) {
        stateErrors[data.name] = errorList[data.name];
      } else {
        delete stateErrors[data.name];
      }

      this.setState({
        ...this.state,
        stateErrors,
      });

      if (selectionChange) {
        selectionChange(data.value);
      }
    };

  ///
  ///This method handles the change in the search text in the autocomplete
  ///InterimData maintains the text changes required to perform the search when the user keeps typing
  ///
  handleAutoComplete_SearchChange = (label, searchChange) => (e, data) => {
    let stateInterimData = this.state.interimData;
    stateInterimData[data.name] = data.value;

    this.setState({
      ...this.state,
      interimData: stateInterimData,
    });

    if (searchChange) {
      searchChange(data.value);
    }
  };

  handleRadioGroup_Change = (label, selectionChanged) => (e, data) => {
    let stateData = this.state.data;
    stateData[data.name] = data.value;
    this.setState({
      ...this.state,
      data: stateData,
    });

    let stateErrors = this.state.stateErrors || {};
    let errorList = this.validateStandardObjects();
    if (errorList && errorList[data.name]) {
      stateErrors[data.name] = errorList[data.name];
    } else {
      delete stateErrors[data.name];
    }

    this.setState({
      ...this.state,
      stateErrors,
    });

    if (selectionChanged) {
      selectionChanged({
        value: data.value,
        name: data.name,
      });
    }
  };

  handleTagInput_Add = (label, itemAddedEvent) => (e, data) => {
    let name = data.name;

    let stateData = this.state.data;
    let array = [];

    for (let item in data.value) {
      array.push({
        key: data.value[item],
        value: data.value[item],
        text: data.value[item],
      });
    }

    stateData[data.name] = array;

    this.setState({
      ...this.state,
      data: stateData,
    });

    let stateErrors = this.state.stateErrors || {};
    let errorList = this.validateStandardObjects();
    if (errorList && errorList[name]) {
      stateErrors[name] = errorList[name];
    } else {
      delete stateErrors[name];
    }

    this.setState({
      stateErrors,
    });

    if (itemAddedEvent) {
      itemAddedEvent(data.value);
    }
  };
}

export default Form;
